import { landmineNames } from '@mm/constants/landmine-names';
import campaignPurposesCRUDActions from '@mm/state/actions/CampaignPurposesCRUDActions';
import campaignsCRUDActions from '@mm/state/actions/CampaignsCRUDActions';
import { CampaignAudienceAttribute } from '@mm/state/model/CampaignsModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { promotionalUpdateName } from '@mm/state/selectors/campaignPurposeSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  associatedAudienceSelector,
  associatedCampaignSelector,
  linkableCampaignsSelector,
} from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import Button from '../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import SelectorWithArrow from '../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import { CHATBOT_EDITOR_DRIP_CAMPAIGN_DETAILS } from '../../../../../common/constants/routes';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import './SetupDripCampaign.pcss';

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StoreProps & DispatchProps & RouteComponentProps;

const SetupDripCampaign = (props: Props) => {
  const {
    botId,
    associatedAudience,
    associatedCampaign,
    campaigns,
    updateCampaign,
    getCampaignPurposes,
    getCampaigns,
    history,
  } = props;

  const blockFeature = !useSelector(combinedFeatureFlagsSelector)
    .adsSetupDripCampain;

  const getOnClickOverride = useCallback(() => {
    if (blockFeature) {
      return () =>
        modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['MESSAGING-ADS__SETUP-DRIP-CAMPAIGN'],
          feature: 'adsSetupDripCampain',
        });
    }
  }, [blockFeature]);

  const onSeeAllClick = useCallback(() => {
    const path = BotBuilderRoutes.audiences.getLink({
      chatbotId: botId,
      audienceId: associatedAudience?.id,
    });
    if (path) {
      history.push(path);
    }
  }, [botId, associatedAudience?.id]);

  const modalActions = useModalActions();

  const [campaignsFetched, setCampaignsFetched] = useState(false);

  useEffect(() => {
    if (!campaignsFetched) {
      getCampaignPurposes();
      getCampaigns(botId);
      setCampaignsFetched(true);
    }
  }, [campaigns, botId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [choice, setChoice] = useState<number | undefined>(undefined);
  const campaignChoices = [
    { id: -1, name: '+ Create a new Drip Campaign' },
    ...campaigns.map(c => ({ id: c.id, name: c.name })),
  ];

  const associatedCampaignId = associatedCampaign?.id;
  const viewAssociatedCampaign = () => {
    if (associatedCampaignId) {
      history.push(
        CHATBOT_EDITOR_DRIP_CAMPAIGN_DETAILS(botId, associatedCampaignId),
      );
    }
  };

  const addToCampaign = () => {
    setChoice(undefined);
    if (choice === -1) {
      history.push(CHATBOT_EDITOR_DRIP_CAMPAIGN_DETAILS(botId), {
        includedAudienceIds: [associatedAudience?.id],
        purpose: promotionalUpdateName,
      });
    } else {
      const selectedCampaign = campaigns.find(c => c.id === choice);
      if (selectedCampaign) {
        const campaignAudiencesAttributes = [
          ...(selectedCampaign.campaignAudiencesAttributes || []),
          {
            audienceId: associatedAudience?.id,
            kind: 'included',
          } as CampaignAudienceAttribute,
        ];
        updateCampaign({
          ...selectedCampaign,
          campaignAudiencesAttributes,
        });
      }
    }
  };

  const unlinkAssociatedCampaign = () => {
    if (associatedCampaign && associatedCampaign.campaignAudiencesAttributes) {
      const campaignAudiencesAttributes =
        associatedCampaign.campaignAudiencesAttributes.map(a =>
          a.audienceId === associatedAudience?.id ? { ...a, destroy: true } : a,
        );
      updateCampaign({
        ...associatedCampaign,
        campaignAudiencesAttributes,
      });
    }
  };

  if (!associatedAudience) {
    return null;
  }

  return (
    <CollapsibleContentContainer
      orderNumber="5"
      containerName="Set up Drip Campaigns (optional)"
      expandedOnMount={!blockFeature}
      proRibbonType="unicorn"
      onClickOverride={getOnClickOverride()}
    >
      <div className="ad-details__section-container ad-details-setup-drip-campaign">
        <div className="ad-details-setup-drip-campaign__box">
          <div>
            Contacts:
            <span className="ad-details-setup-drip-campaign__contact-count">
              {associatedAudience?.contactsCount}
            </span>
          </div>
          <Button link onClick={onSeeAllClick}>
            See all
          </Button>
        </div>
        {associatedCampaign && (
          <>
            <div className="ad-details-setup-drip-campaign__heading">
              Associated Campaign
            </div>
            <div className="ad-details-setup-drip-campaign__box">
              <div>
                {associatedCampaign.name}&nbsp;
                <span className="ad-details-setup-drip-campaign__campaign-status">
                  ({associatedCampaign.displayStatus})
                </span>
              </div>
              <div>
                {['Draft', 'Paused', 'Active'].includes(
                  associatedCampaign.displayStatus,
                ) && (
                  <Button link onClick={unlinkAssociatedCampaign}>
                    Unlink
                  </Button>
                )}
                <Button link onClick={viewAssociatedCampaign}>
                  View
                </Button>
              </div>
            </div>
          </>
        )}
        {!associatedCampaign && (
          <>
            <div className="ad-details-setup-drip-campaign__heading">
              Add to Drip Campaign
            </div>
            <div className="ad-details-setup-drip-campaign__campaign-selection-area">
              <SelectorWithArrow
                className="ad-details-setup-drip-campaign__campaign-selector"
                defaultLabel="Select a Drip Campaign"
                items={campaignChoices}
                selectedItem={campaignChoices.find(c => c.id === choice)}
                onChange={value => setChoice(value as number)}
              />
              <Button
                className="ad-details-setup-drip-campaign__add-button"
                onClick={addToCampaign}
                disabled={choice === undefined}
              >
                {choice === -1 ? 'Create' : 'Add'}
              </Button>
            </div>
          </>
        )}
      </div>
    </CollapsibleContentContainer>
  );
};

const mapStateToProps = (state: StoreState) => ({
  botId: activeChatbotIdSelector(state),
  campaigns: linkableCampaignsSelector(state),
  associatedAudience: associatedAudienceSelector(state),
  associatedCampaign: associatedCampaignSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getCampaignPurposes: () =>
    dispatch(campaignPurposesCRUDActions.retrieveAll()),
  getCampaigns: botId =>
    dispatch(
      campaignsCRUDActions.retrieveAll({
        botId,
        pageNumber: 0,
        pageSize: 999,
        dripCampaign: true,
      }),
    ),
  updateCampaign: campaign =>
    dispatch(campaignsCRUDActions.update(campaign, campaign.id)),
});

const enhance = compose<Props, {}>(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(SetupDripCampaign);
